import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { collection, doc, setDoc, Timestamp } from 'firebase/firestore'
import type { Firestore } from 'firebase/firestore'
import type { IResolvedConfig, IMessage } from '../types'
import { getFirestoreForProjectKey } from '../firebase'
import { useWidgetState } from './hooks/useWidgetState'
import { useChatRoomData } from './hooks/useChatRoomData'
import { useFileUpload } from './hooks/useFileUpload'
import { useMessageSender } from './hooks/useMessageSender'
import { generateMessageId } from './utils/messageUtils'
import { generateThemePalette } from './utils/themeUtils'
import { ensureChatRoomExists, getActiveChatRoomId, checkAndResetIfExpired } from './utils/chatRoomUtils'
import { ChatButton } from './components/ChatButton'
import { ChatHeader } from './components/ChatHeader'
import { MessageBubble } from './components/MessageBubble'
import { TypingIndicator } from './components/TypingIndicator'
import { FilePreview } from './components/FilePreview'
import { MessageInput } from './components/MessageInput'
import { WIDGET_Z_INDEX, WIDGET_WIDTH, WIDGET_HEIGHT } from './constants'
import { useUnreadCount } from './hooks/useUnreadCount'
import { useDraggable } from './hooks/useDraggable'
import type { WidgetMessage, WidgetButton, WidgetPoll } from './types'

export interface WidgetControls {
    open: () => void
    close: () => void
    toggle: () => void
}

interface WidgetProps {
    resolvedConfig: IResolvedConfig
    onControlsReady: (controls: WidgetControls | null) => void
    onStateChange?: () => void
}

export function Widget({
    resolvedConfig,
    onControlsReady,
    onStateChange,
}: WidgetProps) {
    const {
        apiKey,
        clientId,
        userId,
        visitorId: initialVisitorId,
        visitorName: initialVisitorName,
        theme,
        primaryColor,
        useGradient,
        greeting,
        allowStopAI,
        allowFileAttachment,
        openOnLoad,
        floatingEnabled,
        position,
        logoUrl,
        firebaseProjectKey,
        inactivityResetDuration,
    } = resolvedConfig

    // Get Firestore instance (inline simple hook)
    const firestore = useMemo<Firestore | null>(() => {
        if (!firebaseProjectKey) {
            console.error('[Chat Widget] Firebase project key not provided')
            return null
        }
        const firestoreInstance = getFirestoreForProjectKey(firebaseProjectKey)
        if (!firestoreInstance) {
            console.error(`[Chat Widget] Failed to initialize Firestore for project key: ${firebaseProjectKey}`)
        }
        return firestoreInstance
    }, [firebaseProjectKey])

    // visitorId, visitorName, and chatRoomId are dynamic — may change after session reset check
    const [visitorId, setVisitorId] = useState(initialVisitorId)
    const [visitorName, setVisitorName] = useState(initialVisitorName)
    const [chatRoomId, setChatRoomId] = useState(`${clientId}-${initialVisitorId}-${apiKey}`)

    // On widget init: resolve active session and check inactivity expiry
    useEffect(() => {
        if (!firestore || !clientId || !initialVisitorId) return

        const baseChatRoomId = `${clientId}-${initialVisitorId}-${apiKey}`

        async function resolveSession() {
            if (!firestore) return
            // Step 1: Get active chatRoomId from session doc (handles returning visitors post-reset)
            const activeChatRoomId = await getActiveChatRoomId({
                firestore,
                baseChatRoomId,
                clientId,
                visitorId: initialVisitorId,
                apiKey,
            })
            // Step 2: Check if expired, reset if needed
            const result = await checkAndResetIfExpired({
                firestore,
                chatRoomId: activeChatRoomId,
                clientId,
                visitorId: initialVisitorId,
                apiKey,
                inactivityResetDuration,
            })
            setChatRoomId(result.chatRoomId)
            setVisitorId(result.visitorId)
            setVisitorName(result.visitorName)
        }

        resolveSession()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Base greeting message
    const baseGreeting = useMemo<WidgetMessage>(() => {
        return {
            id: generateMessageId('greeting'),
            sender: 'assistant',
            text: greeting,
        }
    }, [greeting])

    // Widget UI state
    const widgetState = useWidgetState(onControlsReady, onStateChange)

    // Auto-open on load if configured
    useEffect(() => {
        if (openOnLoad) {
            widgetState.setIsOpen(true)
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const {
        isOpen,
        setIsOpen,
        isSending,
        setIsSending,
        inputValue,
        setInputValue,
        isTyping,
        setIsTyping,
        messagesEndRef,
    } = widgetState

    // File upload state
    const fileUpload = useFileUpload()

    // Chat room data (combines: initialization, messages, pause status, stop AI)
    const { isPaused, displayedMessages, handleStopAI } = useChatRoomData(
        firestore,
        chatRoomId,
        clientId,
        visitorId,
        visitorName,
        apiKey,
        baseGreeting,
        setIsTyping
    )

    // Message sender
    const { sendMessage } = useMessageSender(
        firestore,
        chatRoomId,
        clientId,
        userId,
        visitorId,
        visitorName,
        inputValue,
        fileUpload,
        isPaused,
        setIsSending,
        setInputValue,
        setIsTyping,
        fileUpload.setFileError,
        firebaseProjectKey as '1' | '2' | '3',
        apiKey
    )

    // Unread message count for badge on collapsed button
    const unreadCount = useUnreadCount(displayedMessages, isOpen, visitorId)

    // Draggable FAB (only active when floatingEnabled)
    const draggable = useDraggable({
        initialCorner: position,
        onDragStart: () => setIsOpen(false),
    })

    // Theme palette
    const palette = useMemo(
        () => generateThemePalette(primaryColor, theme, undefined, useGradient ?? true),
        [primaryColor, theme, useGradient]
    )


    // Check if waiting for poll/button selection
    const waitingForSelection = useMemo(() => {
        if (displayedMessages.length === 0) return false

        // Find the last assistant message
        const lastAssistantMessage = [...displayedMessages].reverse().find(msg => msg.sender === 'assistant')
        if (!lastAssistantMessage) return false

        // Check if it has interactive payload
        if (!lastAssistantMessage.interactivePayload) return false

        // Check if user has already responded after this interactive message
        const lastAssistantIndex = displayedMessages.findIndex(msg => msg.id === lastAssistantMessage.id)
        const hasUserResponseAfter = displayedMessages.slice(lastAssistantIndex + 1).some(msg => msg.sender === 'user')

        return !hasUserResponseAfter
    }, [displayedMessages])

    // Helper function to find user's LATEST response to an interactive message
    const getUserResponseForMessage = useCallback((_messageId: string, messageIndex: number): string | undefined => {
        const msg = displayedMessages[messageIndex]
        if (!msg?.interactivePayload) return undefined

        // Get the options from the interactive payload
        let options: string[] = []
        if (msg.interactivePayload.type === 'buttons' && msg.interactivePayload.buttons) {
            options = msg.interactivePayload.buttons.map(b => b.displayText)
        } else if (msg.interactivePayload.type === 'poll' && msg.interactivePayload.poll) {
            options = msg.interactivePayload.poll.pollOptions || []
        }

        if (options.length === 0) return undefined

        // Look for user's LAST response after this message
        let lastMatch: string | undefined
        for (let i = messageIndex + 1; i < displayedMessages.length; i++) {
            const response = displayedMessages[i]
            if (response.sender === 'assistant') continue

            if (response.text && options.includes(response.text)) {
                lastMatch = response.text
            }
        }

        return lastMatch
    }, [displayedMessages])

    // Handler for poll selection
    const handlePollSelect = useCallback(async (option: string, poll?: WidgetPoll) => {
        if (!firestore || !clientId || !visitorId) return

        setIsSending(true)
        try {
            const now = Timestamp.now()
            const messageId = generateMessageId('user')

            // Construct selectedId for flow continuation (like Hot Assistant)
            // Format: userId:flowId:pollBlockId
            let selectedId: string | undefined
            if (poll?.arrPollItems && poll.userId && poll.flowId) {
                const optionIndex = poll.pollOptions.indexOf(option)
                if (optionIndex !== -1 && poll.arrPollItems[optionIndex]) {
                    selectedId = `${poll.userId}:${poll.flowId}:${poll.arrPollItems[optionIndex]}`
                    console.log('[Chat Widget] Poll selection with selectedId:', selectedId)
                }
            }

            // Ensure chatroom exists before sending poll selection (lazy creation)
            await ensureChatRoomExists({
                firestore,
                chatRoomId,
                clientId,
                visitorId,
                visitorName,
                apiKey,
            })

            const userRecord: IMessage = {
                id: messageId,
                chatRoomId,
                clientId,
                userId,
                body: option,
                from: visitorId,
                to: clientId,
                fromMe: false,
                fromAI: false,
                type: 'chat',
                name: visitorName,
                remoteJid: visitorId,
                object: 'chat_widget',
                date: now,
                createdBy: clientId,
                // Include selectedId for flow continuation
                ...(selectedId ? { selectedId } : {}),
            }

            const messagesCollection = collection(firestore, 'widgetChatRooms', chatRoomId, 'messages')

            await Promise.all([
                setDoc(doc(messagesCollection, messageId), userRecord),
                setDoc(
                    doc(firestore, 'widgetChatRooms', chatRoomId),
                    {
                        date: now,
                        lastMessage: option,
                        lastMessageType: 'chat',
                    },
                    { merge: true },
                ),
            ])

            if (!isPaused) {
                setIsTyping(true)
            }
        } catch (error) {
            console.error('[Chat Widget] Failed to send poll selection', error)
        } finally {
            setIsSending(false)
        }
    }, [firestore, chatRoomId, clientId, userId, visitorId, visitorName, apiKey, isPaused, setIsSending, setIsTyping])

    // Handler for button click
    const handleButtonClick = useCallback(async (button: WidgetButton) => {
        // For URL buttons, window.open is handled in MessageBubble (no message sent)
        // For quick reply buttons, send the display text as a message with button.id as selectedId
        // The button.id is in format "userId:flowId:blockId" which processButton uses for flow continuation
        if (!firestore || !clientId || !visitorId) return

        setIsSending(true)
        try {
            const now = Timestamp.now()
            const messageId = generateMessageId('user')

            // Use button.id as selectedId for flow continuation
            // Backend sets button.id = `${block.createdBy}:${block.flowId}:${pollBlockId}`
            const selectedId = button.id
            console.log('[Chat Widget] Button click with selectedId:', selectedId)

            // Ensure chatroom exists before sending button click (lazy creation)
            await ensureChatRoomExists({
                firestore,
                chatRoomId,
                clientId,
                visitorId,
                visitorName,
                apiKey,
            })

            const userRecord: IMessage = {
                id: messageId,
                chatRoomId,
                clientId,
                userId,
                body: button.displayText,
                from: visitorId,
                to: clientId,
                fromMe: false,
                fromAI: false,
                type: 'chat',
                name: visitorName,
                remoteJid: visitorId,
                object: 'chat_widget',
                date: now,
                createdBy: clientId,
                // Include selectedId for flow continuation
                selectedId,
            }

            const messagesCollection = collection(firestore, 'widgetChatRooms', chatRoomId, 'messages')

            await Promise.all([
                setDoc(doc(messagesCollection, messageId), userRecord),
                setDoc(
                    doc(firestore, 'widgetChatRooms', chatRoomId),
                    {
                        date: now,
                        lastMessage: button.displayText,
                        lastMessageType: 'chat',
                    },
                    { merge: true },
                ),
            ])

            if (!isPaused) {
                setIsTyping(true)
            }
        } catch (error) {
            console.error('[Chat Widget] Failed to send button click', error)
        } finally {
            setIsSending(false)
        }
    }, [firestore, chatRoomId, clientId, userId, visitorId, visitorName, apiKey, isPaused, setIsSending, setIsTyping])

    // Auto-scroll to bottom when messages change
    const messagesContainerRef = useRef<HTMLDivElement | null>(null)
    const messagesContentRef = useRef<HTMLDivElement | null>(null)
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const prevIsOpenRef = useRef(false)

    const animFrameRef = useRef<number | null>(null)
    const scrollToBottom = useCallback((smooth = false) => {
        const container = messagesContainerRef.current
        if (!container) return

        if (!smooth) {
            if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current)
            container.scrollTop = container.scrollHeight
            return
        }

        // Custom smooth scroll with controllable duration (browser default is too fast)
        const start = container.scrollTop
        const end = container.scrollHeight - container.clientHeight
        const distance = end - start
        if (distance <= 0) return

        const duration = 1000 // ms — adjust to taste
        const startTime = performance.now()

        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current)

        const step = (now: number) => {
            const elapsed = now - startTime
            const progress = Math.min(elapsed / duration, 1)
            // Ease-out cubic
            const ease = 1 - Math.pow(1 - progress, 3)
            container.scrollTop = start + distance * ease
            if (progress < 1) {
                animFrameRef.current = requestAnimationFrame(step)
            }
        }
        animFrameRef.current = requestAnimationFrame(step)
    }, [])

    useEffect(() => {
        if (!isOpen) {
            prevIsOpenRef.current = false
            return
        }

        // Clear any pending scroll
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current)
        }

        const justOpened = !prevIsOpenRef.current
        prevIsOpenRef.current = true

        // Smooth scroll for both initial open and new messages.
        // ResizeObserver handles any late image loads with an instant snap.
        scrollTimeoutRef.current = setTimeout(
            () => scrollToBottom(true),
            justOpened ? 300 : 50
        )

        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current)
            }
        }
    }, [displayedMessages, isOpen, isTyping, scrollToBottom])

    // ResizeObserver: instant scroll when content grows (images finishing load).
    // Delayed by 500ms so it doesn't fire during the initial smooth scroll animation.
    const resizeObserverRef = useRef<ResizeObserver | null>(null)
    useEffect(() => {
        if (!isOpen) {
            resizeObserverRef.current?.disconnect()
            resizeObserverRef.current = null
            return
        }

        const connectTimeout = setTimeout(() => {
            const content = messagesContentRef.current
            if (!content) return
            const observer = new ResizeObserver(() => scrollToBottom(false))
            observer.observe(content)
            resizeObserverRef.current = observer
        }, 500)

        return () => {
            clearTimeout(connectTimeout)
            resizeObserverRef.current?.disconnect()
            resizeObserverRef.current = null
        }
    }, [isOpen, scrollToBottom])

    // Generate scrollbar styles based on theme (must be before early return)
    const scrollbarStyles = useMemo(() => {
        const thumbColor = palette.border
        const trackColor = palette.panelSurface
        const hoverColor = palette.textSecondary

        return `
            .widget-messages-container::-webkit-scrollbar {
                width: 8px;
            }
            .widget-messages-container::-webkit-scrollbar-track {
                background: ${trackColor};
                border-radius: 4px;
            }
            .widget-messages-container::-webkit-scrollbar-thumb {
                background: ${thumbColor};
                border-radius: 4px;
            }
            .widget-messages-container::-webkit-scrollbar-thumb:hover {
                background: ${hoverColor};
            }
        `
    }, [palette.panelSurface, palette.border, palette.textSecondary])

    // Early return if Firestore initialization failed
    if (!firestore) {
        console.error('[Chat Widget] Firestore initialization failed')
        return null
    }

    const isSendDisabled = inputValue.trim().length === 0 && !fileUpload.selectedFile

    // Compute floating panel position once — avoids inline recalculation
    const floatingPanelStyle = (() => {
        if (!floatingEnabled || !isOpen) return null
        const fabX = draggable.position.x
        const fabY = draggable.position.y
        const panelW = parseInt(WIDGET_WIDTH, 10)  // 568
        const panelH = parseInt(WIDGET_HEIGHT, 10) // 520
        const vw = window.innerWidth
        const vh = window.innerHeight
        const gap = 12

        // Horizontal: on small screens center the panel, otherwise right-align to FAB
        const fabRightEdge = fabX + 60
        const idealLeft = fabRightEdge - panelW
        const left = vw < panelW + 32
            ? Math.max(0, (vw - Math.min(panelW, vw - 32)) / 2) // center on small screens
            : Math.max(16, Math.min(idealLeft, vw - panelW - 16))

        // Vertical: prefer opening above; if not enough space, open below
        const spaceAbove = fabY - gap
        const spaceBelow = vh - fabY - 60 - gap
        const minPanelH = 400
        const openAbove = spaceAbove >= minPanelH || spaceAbove >= spaceBelow

        const clampedH = openAbove
            ? Math.max(minPanelH, Math.min(panelH, spaceAbove))
            : Math.max(minPanelH, Math.min(panelH, spaceBelow))

        return {
            left: `${left}px`,
            height: `${clampedH}px`,
            ...(openAbove
                ? { bottom: `${vh - fabY + gap}px` }
                : { top: `${fabY + 60 + gap}px` }
            ),
        }
    })()

    return (
        <>
            <style>{scrollbarStyles}</style>

            {/* FAB — draggable in floating mode, fixed bottom-right otherwise */}
            <div
                style={floatingEnabled ? {
                    position: 'fixed',
                    top: `${draggable.position.y}px`,
                    left: `${draggable.position.x}px`,
                    zIndex: WIDGET_Z_INDEX,
                    cursor: draggable.isDragging ? 'grabbing' : 'grab',
                    userSelect: 'none',
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
                } : {
                    position: 'fixed',
                    ...(position === 'top-left'    ? { top: '20px',    left: '20px'  } :
                        position === 'top-right'   ? { top: '20px',    right: '20px' } :
                        position === 'bottom-left' ? { bottom: '20px', left: '20px'  } :
                                                     { bottom: '20px', right: '20px' }),
                    zIndex: WIDGET_Z_INDEX,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: position === 'bottom-left' || position === 'top-left' ? 'flex-start' : 'flex-end',
                    gap: '12px',
                }}
                {...(floatingEnabled ? {
                    onMouseDown: draggable.onMouseDown,
                    onTouchStart: draggable.onTouchStart,
                } : {})}
            >
                {!floatingEnabled && isOpen && (
                    <div
                        onMouseDown={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        style={{
                            width: WIDGET_WIDTH,
                            maxWidth: 'calc(100vw - 32px)',
                            height: WIDGET_HEIGHT,
                            maxHeight: 'calc(100vh - 110px)',
                            borderRadius: '24px',
                            boxShadow: '0 25px 60px rgba(15,23,42,0.35)',
                            background: palette.panelBg,
                            color: palette.textPrimary,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            border: `1px solid ${palette.border}`,
                            backdropFilter: 'blur(12px)',
                            position: 'relative',
                            cursor: 'default',
                            userSelect: 'text',
                        }}
                    >
                        <ChatHeader isPaused={isPaused} onStopAI={handleStopAI} palette={palette} allowStopAI={allowStopAI} logoUrl={logoUrl} />

                        <div
                            ref={messagesContainerRef}
                            className="widget-messages-container"
                            style={{
                                flex: 1,
                                padding: '18px 16px',
                                fontSize: '14px',
                                color: palette.textPrimary,
                                background: palette.panelSurface,
                                overflowY: 'auto',
                                overflowX: 'hidden',
                            }}
                        >
                            <div
                                ref={messagesContentRef}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '14px',
                                }}
                            >
                                {displayedMessages.map((msg, index) => (
                                    <MessageBubble
                                        key={msg.id}
                                        message={msg}
                                        palette={palette}
                                        onPollSelect={handlePollSelect}
                                        onButtonClick={handleButtonClick}
                                        selectedOption={getUserResponseForMessage(msg.id, index)}
                                    />
                                ))}

                                {isTyping && <TypingIndicator palette={palette} />}

                                <div ref={messagesEndRef} />
                            </div>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                            }}
                        >
                            {fileUpload.selectedFile && (
                                <div style={{ padding: '0 16px' }}>
                                    <FilePreview
                                        file={fileUpload.selectedFile}
                                        preview={fileUpload.filePreview}
                                        onRemove={fileUpload.handleRemoveFile}
                                        palette={palette}
                                    />
                                </div>
                            )}

                            <MessageInput
                                inputValue={inputValue}
                                onInputChange={setInputValue}
                                onSend={sendMessage}
                                onFileSelect={fileUpload.handleFileSelect}
                                fileInputRef={fileUpload.fileInputRef}
                                isSending={isSending}
                                isSendDisabled={isSendDisabled || waitingForSelection}
                                fileError={fileUpload.fileError}
                                onClearFileError={fileUpload.clearFileError}
                                palette={palette}
                                allowFileAttachment={allowFileAttachment}
                                disabled={waitingForSelection}
                                placeholder={waitingForSelection ? 'Please select an option above' : undefined}
                            />
                        </div>
                    </div>
                )}

                <ChatButton
                    isOpen={isOpen}
                    onClick={() => {
                        if (floatingEnabled && draggable.wasDrag) return
                        setIsOpen((prev) => !prev)
                    }}
                    palette={palette}
                    logoUrl={logoUrl}
                    unreadCount={unreadCount}
                />
            </div>

            {/* Floating panel — rendered independently so it's not constrained by FAB container */}
            {floatingEnabled && isOpen && floatingPanelStyle && (
                <div
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    style={{
                        position: 'fixed',
                        ...floatingPanelStyle,
                        width: WIDGET_WIDTH,
                        maxWidth: 'calc(100vw - 32px)',
                        borderRadius: '24px',
                        boxShadow: '0 25px 60px rgba(15,23,42,0.35)',
                        background: palette.panelBg,
                        color: palette.textPrimary,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        border: `1px solid ${palette.border}`,
                        backdropFilter: 'blur(12px)',
                        cursor: 'default',
                        userSelect: 'text',
                        zIndex: WIDGET_Z_INDEX,
                        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
                    }}
                >
                    <ChatHeader isPaused={isPaused} onStopAI={handleStopAI} palette={palette} allowStopAI={allowStopAI} logoUrl={logoUrl} />
                    <div
                        ref={messagesContainerRef}
                        className="widget-messages-container"
                        style={{ flex: 1, padding: '18px 16px', fontSize: '14px', color: palette.textPrimary, background: palette.panelSurface, overflowY: 'auto', overflowX: 'hidden' }}
                    >
                        <div ref={messagesContentRef} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            {displayedMessages.map((msg, index) => (
                                <MessageBubble key={msg.id} message={msg} palette={palette} onPollSelect={handlePollSelect} onButtonClick={handleButtonClick} selectedOption={getUserResponseForMessage(msg.id, index)} />
                            ))}
                            {isTyping && <TypingIndicator palette={palette} />}
                            <div ref={messagesEndRef} />
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {fileUpload.selectedFile && (
                            <div style={{ padding: '0 16px' }}>
                                <FilePreview file={fileUpload.selectedFile} preview={fileUpload.filePreview} onRemove={fileUpload.handleRemoveFile} palette={palette} />
                            </div>
                        )}
                        <MessageInput
                            inputValue={inputValue}
                            onInputChange={setInputValue}
                            onSend={sendMessage}
                            onFileSelect={fileUpload.handleFileSelect}
                            fileInputRef={fileUpload.fileInputRef}
                            isSending={isSending}
                            isSendDisabled={isSendDisabled || waitingForSelection}
                            fileError={fileUpload.fileError}
                            onClearFileError={fileUpload.clearFileError}
                            palette={palette}
                            allowFileAttachment={allowFileAttachment}
                            disabled={waitingForSelection}
                            placeholder={waitingForSelection ? 'Please select an option above' : undefined}
                        />
                    </div>
                </div>
            )}
        </>
    )
}
